import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

/**
 * KobuNav — editorial top bar in the Kobu system. Parchment canvas, obsidian
 * ink, Fira Mono tracked links, a single hairline bottom border, no gold, no
 * shadow. Pages adopt this as they convert to Kobu (the dark SiteNav remains
 * for not-yet-converted pages). Preserves SiteNav's logic: active-link state,
 * mobile menu + backdrop, route-change auto-close, 44px touch targets.
 */

const NAV_LINKS = [
  { label: 'Day Planner', href: '/day-planner' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Journal', href: '/journal' },
  { label: 'About', href: '/about' },
  { label: 'FAQ', href: '/faq' },
];

interface KobuNavProps {
  /** 'solid' (default) sits on parchment; 'transparent' lets hero imagery show through. */
  variant?: 'solid' | 'transparent';
}

const KobuNav: React.FC<KobuNavProps> = ({ variant = 'solid' }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  const isActive = (href: string) => location.pathname === href;

  const surface = variant === 'transparent'
    ? 'bg-parchment/80 backdrop-blur-md'
    : 'bg-parchment';

  return (
    <nav aria-label="Main navigation" className={`relative w-full z-50 border-b border-obsidian ${surface}`}>
      <div className="max-w-[1440px] mx-auto px-6 sm:px-10 h-[60px] flex items-center justify-between">
        {/* Wordmark */}
        <Link to="/" aria-label="Aura Traveler home" className="kobu-label text-obsidian hover:text-ash-gray transition-colors">
          Aura Traveler
        </Link>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-7">
          {NAV_LINKS.map(({ label, href }) => (
            <Link
              key={href}
              to={href}
              className={`kobu-caption transition-colors ${isActive(href) ? 'text-obsidian' : 'text-ash-gray hover:text-obsidian'}`}
            >
              {label}
            </Link>
          ))}
          <Link
            to="/day-planner"
            className="kobu-label bg-obsidian text-parchment px-5 py-2.5 hover:bg-charcoal-deep transition-colors"
          >
            Plan My Day
          </Link>
        </div>

        {/* Mobile: CTA + hamburger */}
        <div className="md:hidden flex items-center gap-3">
          <Link to="/day-planner" className="kobu-label bg-obsidian text-parchment px-4 py-2.5 min-h-[44px] inline-flex items-center">
            Plan
          </Link>
          <button
            aria-label="Toggle menu"
            className="w-11 h-11 flex flex-col justify-center items-center gap-1.5"
            onClick={() => setMobileOpen(v => !v)}
          >
            <span className={`block w-6 h-px bg-obsidian transition-all duration-300 ${mobileOpen ? 'rotate-45 translate-y-[5px]' : ''}`} />
            <span className={`block w-6 h-px bg-obsidian transition-all duration-300 ${mobileOpen ? '-rotate-45 -translate-y-[3px]' : ''}`} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 bg-obsidian/20 z-40 md:hidden" onClick={() => setMobileOpen(false)} />
          <div className="absolute top-full left-0 w-full bg-parchment border-b border-obsidian py-6 flex flex-col items-center gap-5 md:hidden z-50">
            {NAV_LINKS.map(({ label, href }) => (
              <Link
                key={href}
                to={href}
                className={`kobu-caption min-h-[44px] flex items-center transition-colors ${isActive(href) ? 'text-obsidian' : 'text-ash-gray'}`}
              >
                {label}
              </Link>
            ))}
          </div>
        </>
      )}
    </nav>
  );
};

export default KobuNav;
